import { Col, Div, Dropdown, Row, Text, Icon, Switch as ToggleSwitch } from '@pinpoint/design-kit';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import CONSTANTS from 'src/store/constants';
import { Card, Message, Switch, Button } from 'src/components';
import { ApiResponseProp, ModelProp } from 'src/store/typings';
import { Rbac } from 'src/containers';

import { ApiReturnTimeCardDefault } from './return_default';
import { ApiReturnTimeCardAdvanced } from './return_advanced';

const menuItems = CONSTANTS.REAL_TIME_API.USAGE.FILTERS.map((filter) => (
  <Div k={filter} key={filter}>
    {CONSTANTS.REAL_TIME_API.USAGE.FILTERS_DESC[filter]}
  </Div>
));

export function ApiReturnTimeCard({
  client,
  config,
  configId,
  generateStatsStatus,
  rttReady,
  usageFilter,
  rttEntries,
  rttSecondaryEntries,
  rttToDateEntries,
  hist,
  stats,
  toggleView,
  selectUsageFilter,
  loadRtApiUsage,
}) {
  const [rangeSelectionOpen, setRangeSelectionOpen] = useState(false);
  const [showAdvancedView, setShowAdvancedView] = useState(false);

  const toggleRangeSelectionOpen = useCallback(
    () => setRangeSelectionOpen(!rangeSelectionOpen),
    [setRangeSelectionOpen, rangeSelectionOpen]
  );

  return (
    <Card
      title={
        rttReady === CONSTANTS.API.SUCCEEDED ? (
          <Div d="flex">
            <Text textWeight="600">Return Times in Milliseconds{configId ? ':' : ''}</Text>
            <Text>&ensp;{config?.config_metadata?.model?.name}</Text>
          </Div>
        ) : null
      }
      iconProps={{
        'data-debug': 'card-icon-debug',
        name: 'RtRetTime',
        color: 'white',
        size: '3.125rem',
        viewBox: '0 0 18 16',
      }}
      titleProps={{
        'data-debug': 'card-title-debug',
        bg: 'marigold',
        h: '5.5rem',
        p: { l: '2.5rem', r: '2.625rem' },
        rounded: { tr: 'md', tl: 'md' },
      }}
      titleTextProps={{
        'data-debug': 'card-title-text-debug',
        textColor: 'white',
        textSize: 'title',
        lineHeight: 'title',
      }}
      cardProps={{
        'data-debug': 'card-debug',
        p: '0',
        textSize: 'paragraph',
        style: {
          lineHeight: '1.5rem',
        },
        rounded: 'md',
        textColor: 'shade66',
        d: 'flex',
        flexDir: 'column',
      }}
    >
      <Switch status={rttReady}>
        <Message bg="white" value={CONSTANTS.API.STARTED} />
        <Message type="error" bg="white" value={CONSTANTS.API.FAILED} />
        <Col value={CONSTANTS.API.SUCCEEDED} gutterWidth="0" size="12" flexGrow="1" d="flex" flexDir="column">
          <Row p={{ t: '3.375rem', x: '2.5rem' }} flexDir="row">
            <Col gutterWidth="0" size="2" align="flex-start" d="flex" flexDir="column">
              <Rbac roles={CONSTANTS.ROLES.slice(0, 4)}>
                <Div d="flex">
                  <Button buttonStyle="admin" onClick={toggleView}>
                    <Icon name="RtUsage" viewBox="0 0 18 18" color="white" m={{ r: '.5rem' }} />
                    REQUESTS
                  </Button>
                  <Button buttonStyle="admin" disabled>
                    <Icon name="RtRetTime" viewBox="0 0 15 17" color="white" m={{ r: '.5rem' }} />
                    RETURN TIMES
                  </Button>
                </Div>
              </Rbac>
              <Div textColor="shade66" textSize="input" textWeight="500" p={{ t: '.75rem' }} m={{ y: '.25rem' }}>
                Median Milliseconds
              </Div>
            </Col>
            <Col size="9" textColor="shade66" textSize="small" data-debug="col" d="flex" align="center">
              <Div textColor="shade66" textSize="input" textWeight="500" alignSelf="baseline" p={{ l: '.5rem' }}>
                Show Return Times for
              </Div>
              <Dropdown
                alignSelf="baseline"
                h="2rem"
                textSize="1rem"
                w="14rem"
                m={{ l: '1rem' }}
                isOpen={rangeSelectionOpen}
                onClick={toggleRangeSelectionOpen}
                defaultValue={menuItems[CONSTANTS.REAL_TIME_API.USAGE.FILTERS.findIndex((fl) => fl === usageFilter)]}
                value={CONSTANTS.REAL_TIME_API.USAGE.FILTERS.findIndex((filt) => filt === usageFilter)}
                menu={menuItems}
                onSelect={selectUsageFilter}
                rounded={rangeSelectionOpen ? { tl: 'md', bl: '0', br: '0', tr: 'md' } : 'md'}
                border={rangeSelectionOpen ? { x: '1px solid', t: '1px solid', b: '0' } : '1px solid'}
                style={{
                  transition: 'all 0.2s ease-in-out',
                }}
                menuItemOptions={{
                  p: { y: '.25rem', x: '.75rem' },
                  textWeight: '500',
                  bg: 'shadeF2',
                  hoverBg: 'white',
                  style: {
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
                menuOptions={{
                  border: { b: '1px solid', x: '1px solid', t: '0' },
                  rounded: { bl: 'md', br: 'md', tr: '0', tl: '0' },
                  pos: 'absolute',
                  shadow: 4,
                  minW: '14rem',
                  maxW: '14rem',
                  style: {
                    transition: 'all 0.2s ease-in-out',
                  },
                  maxH: '250px',
                  overflow: 'auto',
                  textColor: 'shade66',
                  m: { t: '-2px', l: '-1px' },
                }}
              />
            </Col>
            <Rbac roles={CONSTANTS.ROLES.slice(0, 4)}>
              <Col gutterWidth="0" size="1" align="flex-end" d="flex" flexDir="column">
                <Row w="100%" m="0" align="center" justify="flex-end" p={{ t: '.25rem' }}>
                  <Text textColor="shade66" textSize="input" textWeight="500" m={{ r: '.5rem' }}>
                    Advanced
                  </Text>
                  <Div
                    align="center"
                    justify="flex-end"
                    data-test="advanced-toggle"
                    cursor="pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const nextValue = !showAdvancedView;
                      setShowAdvancedView(nextValue);
                    }}
                  >
                    <ToggleSwitch
                      checked={showAdvancedView}
                      activeColor="marigold"
                      inactiveColor="shadeA0"
                      activeShadow="0"
                      inactiveShadow="0"
                      p="0"
                      m="0"
                    />
                  </Div>
                </Row>
              </Col>
            </Rbac>
          </Row>
          {!showAdvancedView ? (
            <ApiReturnTimeCardDefault
              client={client}
              generateStatsStatus={generateStatsStatus}
              rttEntries={rttEntries}
              rttSecondaryEntries={rttSecondaryEntries}
              rttToDateEntries={rttToDateEntries}
              usageFilter={usageFilter}
            />
          ) : (
            <ApiReturnTimeCardAdvanced hist={hist} rttReady={rttReady} stats={stats} usageFilter={usageFilter} />
          )}
        </Col>
        <Message
          value={null}
          bg="white"
          type="info"
          text="No data"
          cta={{ text: 'Refresh', onClick: loadRtApiUsage }}
        />
      </Switch>
    </Card>
  );
}

ApiReturnTimeCard.defaultProps = {
  config: null,
  configId: null,
  generateStatsStatus: null,
  rttReady: null,
};

ApiReturnTimeCard.propTypes = {
  // component
  client: PropTypes.shape({
    billingDay: PropTypes.number.isRequired,
    enableRealTimeAPICaching: PropTypes.bool.isRequired,
  }).isRequired,
  usageFilter: PropTypes.string.isRequired,
  // rtApiUsage store
  generateStatsStatus: ApiResponseProp,
  rttReady: ApiResponseProp,
  configId: PropTypes.string,
  config: PropTypes.shape({
    id: PropTypes.string.isRequired,
    config_metadata: PropTypes.shape({
      model: ModelProp,
    }).isRequired,
  }),
  rttEntries: PropTypes.arrayOf(
    PropTypes.shape({
      period: PropTypes.string,
      count: PropTypes.number,
      meta: PropTypes.string,
    })
  ).isRequired,
  rttSecondaryEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rttToDateEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hist: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
      left: PropTypes.number.isRequired,
      right: PropTypes.number.isRequired,
    })
  ).isRequired,
  masks: PropTypes.shape({
    ytdMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    mtdMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    mtdTotal: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    tdyMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    tdyTotal: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    btdUnmatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
  }).isRequired,
  stats: PropTypes.shape({
    mean: PropTypes.number.isRequired,
    p50: PropTypes.number.isRequired,
    p75: PropTypes.number.isRequired,
    p90: PropTypes.number.isRequired,
  }).isRequired,
  // functions
  selectUsageFilter: PropTypes.func.isRequired,
  toggleView: PropTypes.func.isRequired,
  loadRtApiUsage: PropTypes.func.isRequired,
};
